import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from 'classnames';

import Button from "components/inputs/Button";
import SVGIcon from "components/SVGIcon";

import { svgProps } from 'components/icons/SvgContainer';


// I considered just adding a `dropdownOptions` prop to the <Button> component
// but the dropdown requires the parent <div> added to its markup.

const ButtonDropdown = ({
  options,
  children,
  styleVariant,
  className = '',
  buttonClassName = '',
  menuClassName = '',
  menuStyle,
  large = false,
  small = false,
  icon,
  iconProps = { height: 16, width: 16 },
  xClose,
  caretToRight = false,
  showCaret = true,
  openUp = false,
  openOnHover = true,
  disabled = false
}) => {
  const [showDrop, toggleDropdown] = useState(false);
  const dropDownClass = classNames("dropdown-menu", menuClassName, { show: showDrop });

  const handleFocusIn = () => {
    if (!disabled && openOnHover) {
      toggleDropdown(true);
    }
  };

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      toggleDropdown(false);
    }
  };

  return (
    <div
      style={{ pointerEvents: disabled ? 'none' : 'all' }}
      className={classNames('ButtonDropdown', { dropdown: !openUp, dropup: openUp }, className)}
      onMouseOver={handleFocusIn}
      onFocus={handleFocusIn}
      onMouseOut={() => openOnHover && toggleDropdown(false)}
      onBlur={handleBlur}
    >
      <Button
        large={large}
        small={small}
        icon={icon}
        iconProps={iconProps}
        styleVariant={styleVariant}
        className={buttonClassName}
        caretToRight={caretToRight}
        disabled={disabled}
      >
        {children}
        {showCaret && <div className={`btn-caret${caretToRight ? ' pr-2' : ''}`} />}
        {showCaret && <SVGIcon name="DownCaret1" width={12} height={12} />}
      </Button>
      <div className={dropDownClass} style={menuStyle}>
        {Object.keys(options).map((key) => {
          const option = options[key];
          const optionConfig = typeof option === 'function' ? { onClick: option } : option;
          const {
            onClick,
            icon: optionIcon,
            dividerBefore,
            itemClassName = '',
            iconProps: optionIconProps = { width: 22, height: 22 },
          } = optionConfig;

          return (
            <React.Fragment key={key}>
              {dividerBefore && <div className="dropdown-divider my-2" />}
              <button
                type="button"
                className={`dropdown-item ${itemClassName}`}
                onClick={onClick}
                onTouchEnd={onClick}
              >
                {!xClose ? (
                  <div className="d-flex align-items-center">
                    {optionIcon && <SVGIcon name={optionIcon} {...optionIconProps} />}
                    <span className={optionIcon ? 'ml-3' : ''}>{key}</span>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between" aria-label="Remove">
                    <span className="pr-4">{key}</span><span>X</span>
                  </div>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

ButtonDropdown.propTypes = {
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  children: PropTypes.node,
  menuClassName: PropTypes.string,
  menuStyle: PropTypes.object,
  options: PropTypes.objectOf(PropTypes.func),
  styleVariant: PropTypes.oneOf([
    'primary',
    'light',
    'outline-primary',
    'outline-light',
    'none',
    'success',
    'danger'
  ]),
  large: PropTypes.bool,
  small: PropTypes.bool,
  icon: PropTypes.string,
  iconProps: PropTypes.shape({ ...svgProps }),
  xClose: PropTypes.bool,
  caretToRight: PropTypes.bool,
  showCaret: PropTypes.bool,
  openUp: PropTypes.bool,
  openOnHover: PropTypes.bool,
  disabled: PropTypes.bool
};

export default ButtonDropdown;
